import * as THREE from 'three';
import { MARKERS } from '../config/tuning';
import type { TargetInstance } from '../combat/TargetField';
import {
  createBeaconGeometry,
  createCheckpointGeometry,
  createPostGeometry,
} from './generators/MarkerGen';

/**
 * The world markers: a ring on the next checkpoint, a beacon on every target still standing.
 *
 * The theatre is ten kilometres of coastline that all looks alike, and until now the only thing
 * saying *where* was a label on the horizon. A label tells you a bearing; it does not tell you
 * which of the four buildings below you is the one. These do.
 *
 * Presentation only: nothing here is simulated, nothing enters the determinism checksum, and the
 * update runs on the render clock. The one thing that must stay deterministic is the pulse, which
 * is driven by simulated time like every other blink in the game (see `HudFrame.simTimeS`) so a
 * replay of the same flight looks the same.
 *
 * Geometry is built once and shared; per-frame work is a transform and an opacity.
 */

export class MarkerRenderer {
  readonly object3d = new THREE.Group();

  private readonly checkpointGeometry: THREE.BufferGeometry;
  private readonly postGeometry: THREE.BufferGeometry;
  private readonly beaconGeometry: THREE.BufferGeometry;
  private readonly checkpointMaterial: THREE.LineBasicMaterial;
  private readonly beaconMaterial: THREE.LineBasicMaterial;

  private readonly checkpoint: THREE.LineSegments;
  private readonly post: THREE.LineSegments;
  private readonly beacons: THREE.LineSegments[] = [];

  constructor() {
    this.checkpointGeometry = createCheckpointGeometry();
    this.postGeometry = createPostGeometry();
    this.beaconGeometry = createBeaconGeometry();
    this.checkpointMaterial = createMaterial(MARKERS.checkpointColour);
    this.beaconMaterial = createMaterial(MARKERS.targetColour);

    this.checkpoint = new THREE.LineSegments(this.checkpointGeometry, this.checkpointMaterial);
    this.checkpoint.visible = false;
    // The marker is placed every frame; letting Three recompute a bounding sphere from a unit ring
    // it then scales would cull it at the wrong ranges.
    this.checkpoint.frustumCulled = false;
    this.object3d.add(this.checkpoint);

    this.post = new THREE.LineSegments(this.postGeometry, this.checkpointMaterial);
    this.post.visible = false;
    this.post.frustumCulled = false;
    this.object3d.add(this.post);
  }

  /** Rebuild the beacon set for a new mission's targets. */
  rebuild(targets: readonly TargetInstance[]): void {
    for (let i = 0; i < this.beacons.length; i += 1) this.object3d.remove(this.beacons[i]);
    this.beacons.length = 0;

    for (let i = 0; i < targets.length; i += 1) {
      const beacon = new THREE.LineSegments(this.beaconGeometry, this.beaconMaterial);
      beacon.frustumCulled = false;
      // The beacon stands on the target's base, not on its aim point: an aim point can be half way
      // up a building, and a column growing out of a roof reads as part of the building.
      beacon.position.copy(targets[i].positionM);
      beacon.visible = !targets[i].destroyed;
      this.beacons.push(beacon);
      this.object3d.add(beacon);
    }
  }

  /**
   * Place the checkpoint ring and hide the beacons of anything destroyed.
   *
   * `simTimeS` drives the pulse. `checkpoint` is null when no `reach` objective is outstanding, and
   * the ring simply goes away — a marker on a checkpoint already passed would be an instruction to
   * fly backwards. `groundM` is the surface height under the checkpoint, which is what the post is
   * drawn down to.
   */
  update(
    checkpoint: { positionM: THREE.Vector3; radiusM: number; groundM: number } | null,
    targets: readonly TargetInstance[],
    simTimeS: number,
  ): void {
    const pulse =
      1 - MARKERS.pulseDepth * 0.5 * (1 - Math.cos((simTimeS / MARKERS.pulsePeriodS) * Math.PI * 2));
    this.checkpointMaterial.opacity = MARKERS.opacity * pulse;
    this.beaconMaterial.opacity = MARKERS.opacity * pulse;

    if (checkpoint === null) {
      this.checkpoint.visible = false;
      this.post.visible = false;
    } else {
      this.checkpoint.visible = true;
      this.checkpoint.position.copy(checkpoint.positionM);
      this.checkpoint.scale.setScalar(checkpoint.radiusM);

      // The post ends at the surface. Sized from the terrain rather than from the ring, because a
      // checkpoint out at sea is the common case on a coastal ingress and a fixed drop ran on
      // below the waterline.
      const drop = checkpoint.positionM.y - checkpoint.groundM;
      this.post.visible = drop > MARKERS.minPostM;
      this.post.position.copy(checkpoint.positionM);
      this.post.scale.y = drop;
    }

    // Targets can be destroyed mid-run, and the beacon going out is the first thing that says so —
    // it beats the wreckage geometry by the length of the impact cam.
    const count = Math.min(this.beacons.length, targets.length);
    for (let i = 0; i < count; i += 1) {
      this.beacons[i].visible = !targets[i].destroyed;
    }
  }

  dispose(): void {
    this.postGeometry.dispose();
    this.checkpointGeometry.dispose();
    this.beaconGeometry.dispose();
    this.checkpointMaterial.dispose();
    this.beaconMaterial.dispose();
  }
}

/** Marker line material: unlit, unfogged, depth-tested. See `MARKERS` for why those two differ. */
function createMaterial(colour: number): THREE.LineBasicMaterial {
  return new THREE.LineBasicMaterial({
    color: colour,
    transparent: true,
    opacity: MARKERS.opacity,
    fog: false,
    depthWrite: false,
  });
}
